import { Label } from '../../../components/ui/label';
import { Input } from '../../../components/ui/input';
import MultiSelectDropdownNoCreate from '../../dropdowns/MultiSelectDropdownNoCreate';
import { FundraisingBarConfigurationTabProps } from './types';
import { formatWithCommas, stripNonNumeric } from '../../../utils/formatters';

export default function FundraisingBarConfiguration(props: FundraisingBarConfigurationTabProps) {
  const { formData, updateFormData, availableForms, handleFieldFocus, isFieldInvalid, invalidFields, setInvalidFields } = props;

  const { elementName, connectedForms, goal, elementOffset, trackingMetric, metricLabel } = formData;

  const isCountMetric = trackingMetric === 'SUBSCRIPTION_COUNT' || trackingMetric === 'PRODUCT_COUNT' || trackingMetric === 'DONATION_COUNT';

  const setConnectedForms = (forms: string[]) => {
    updateFormData('connectedForms', forms);
    handleFieldFocus('configuration-connectedForms');
  };

  return (
    <>
      {/* Element Name */}
      <div className="space-y-0">
        <Label
          htmlFor="element-name"
          className="font-inter text-base font-medium leading-6 text-ink-secondary"
        >
          Element Name
        </Label>
        <Input
          id="element-name"
          value={elementName}
          onChange={(e) => {
            updateFormData('elementName', e.target.value);
          }}
          onFocus={() => handleFieldFocus('configuration-elementName')}
          className={`h-[28px] flex-shrink-0 self-stretch ${
            isFieldInvalid('elementName') ? 'border-red-500' : ''
          }`}
        />
      </div>

      {/* Tracking Metric — 2-Button Segmented + Radio Sub-Selector */}
      <div className="space-y-2">
        <Label className="font-inter text-base font-medium leading-6 text-ink-secondary">
          Metric
        </Label>
        <div className="flex rounded-md border border-edge bg-paper-inset p-0.5">
          <button
            type="button"
            onClick={() => updateFormData('trackingMetric', 'DOLLAR_AMOUNT')}
            className={`flex flex-1 items-center justify-center gap-1 rounded-[4px] border p-1.5 text-[11px] font-medium transition-colors ${
              trackingMetric === 'DOLLAR_AMOUNT' || !trackingMetric
                ? 'border-edge bg-paper-light text-ink-secondary'
                : 'border-transparent text-ink-muted hover:text-ink-secondary'
            }`}
          >
            Dollar Amount
          </button>
          <button
            type="button"
            onClick={() => {
              if (trackingMetric === 'DOLLAR_AMOUNT' || !trackingMetric) {
                updateFormData('trackingMetric', 'SUBSCRIPTION_COUNT');
              }
            }}
            className={`flex flex-1 items-center justify-center gap-1 rounded-[4px] border p-1.5 text-[11px] font-medium transition-colors ${
              trackingMetric !== 'DOLLAR_AMOUNT' && trackingMetric
                ? 'border-edge bg-paper-light text-ink-secondary'
                : 'border-transparent text-ink-muted hover:text-ink-secondary'
            }`}
          >
            Count
          </button>
        </div>

        {/* Conditional config section */}
        <div className="space-y-2 rounded-md border border-edge bg-paper-inset p-2">
          {isCountMetric ? (
            <>
              {/* Count Type — Radio Sub-Selector */}
              <div className="flex flex-col sm:flex-row gap-1 sm:gap-8">
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="bar-count-type"
                    value="SUBSCRIPTION_COUNT"
                    className="h-4 w-4 cursor-pointer rounded-full bg-white accent-sage focus:ring-sage border-ink-secondary text-sage"
                    checked={trackingMetric === 'SUBSCRIPTION_COUNT'}
                    onChange={() => updateFormData('trackingMetric', 'SUBSCRIPTION_COUNT')}
                  />
                  <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                    Subscriptions
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="bar-count-type"
                    value="PRODUCT_COUNT"
                    className="h-4 w-4 cursor-pointer rounded-full bg-white accent-sage focus:ring-sage border-ink-secondary text-sage"
                    checked={trackingMetric === 'PRODUCT_COUNT'}
                    onChange={() => updateFormData('trackingMetric', 'PRODUCT_COUNT')}
                  />
                  <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                    Items
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="bar-count-type"
                    value="DONATION_COUNT"
                    className="h-4 w-4 cursor-pointer rounded-full bg-white accent-sage focus:ring-sage border-ink-secondary text-sage"
                    checked={trackingMetric === 'DONATION_COUNT'}
                    onChange={() => updateFormData('trackingMetric', 'DONATION_COUNT')}
                  />
                  <Label className="block font-inter text-sm font-medium leading-5 text-ink-muted">
                    Donations
                  </Label>
                </div>
              </div>

              {/* Count Goal */}
              <div className="space-y-0">
                <Label htmlFor="goal" className="font-inter text-sm font-medium leading-6 text-ink-secondary">
                  {trackingMetric === 'PRODUCT_COUNT' ? 'Items Goal'
                    : trackingMetric === 'DONATION_COUNT' ? 'Donations Goal'
                    : 'Subscriptions Goal'}
                </Label>
                <Input
                  id="goal"
                  type="text"
                  inputMode="numeric"
                  value={formatWithCommas(goal)}
                  onChange={(e) => {
                    updateFormData('goal', stripNonNumeric(e.target.value));
                  }}
                  onFocus={() => handleFieldFocus('configuration-goal')}
                  className={`h-[28px] flex-shrink-0 self-stretch rounded-md border ${
                    isFieldInvalid('goal') ? 'border-red-500' : 'border-edge'
                  } bg-white`}
                  placeholder="100"
                />
              </div>

              {/* Count Offset */}
              <div className="space-y-0">
                <Label htmlFor="element-offset" className="font-inter text-sm font-medium leading-6 text-ink-secondary">
                  Count Offset
                </Label>
                <Input
                  id="element-offset"
                  type="text"
                  inputMode="numeric"
                  value={formatWithCommas(elementOffset)}
                  onChange={(e) => {
                    updateFormData('elementOffset', stripNonNumeric(e.target.value));
                  }}
                  onFocus={() => handleFieldFocus('configuration-elementOffset')}
                  className={`h-[28px] flex-shrink-0 self-stretch rounded-md border ${
                    isFieldInvalid('elementOffset') ? 'border-red-500' : 'border-edge'
                  } bg-white`}
                  placeholder="0"
                />
              </div>

              {/* Display Label */}
              <div className="space-y-0">
                <Label htmlFor="metric-label" className="font-inter text-sm font-medium leading-6 text-ink-secondary">
                  Display Label
                </Label>
                <Input
                  id="metric-label"
                  type="text"
                  value={metricLabel}
                  onChange={(e) => {
                    if (e.target.value.length <= 18) {
                      updateFormData('metricLabel', e.target.value);
                    }
                  }}
                  onFocus={() => handleFieldFocus('configuration-metricLabel')}
                  className={`h-[28px] flex-shrink-0 self-stretch rounded-md border ${
                    isFieldInvalid('metricLabel') ? 'border-red-500' : 'border-edge'
                  } bg-white`}
                  placeholder={
                    trackingMetric === 'PRODUCT_COUNT' ? 'Items'
                      : trackingMetric === 'DONATION_COUNT' ? 'Payments'
                      : 'Sustainers'
                  }
                  maxLength={18}
                />
                <p className="text-xs text-ink-muted mt-0.5">
                  {trackingMetric === 'PRODUCT_COUNT'
                    ? "The word shown after the count (e.g. '42 Items')"
                    : trackingMetric === 'DONATION_COUNT'
                    ? "The word shown after the count (e.g. '312 Donations')"
                    : "The word shown after the count (e.g. '312 Sustainers')"}
                </p>
              </div>
            </>
          ) : (
            <>
              {/* Dollar Goal */}
              <div className="space-y-0">
                <Label htmlFor="goal" className="font-inter text-sm font-medium leading-6 text-ink-secondary">
                  Goal
                </Label>
                <Input
                  id="goal"
                  type="text"
                  inputMode="numeric"
                  value={formatWithCommas(goal)}
                  onChange={(e) => {
                    updateFormData('goal', stripNonNumeric(e.target.value));
                  }}
                  onFocus={() => handleFieldFocus('configuration-goal')}
                  className={`h-[28px] flex-shrink-0 self-stretch rounded-md border ${
                    isFieldInvalid('goal') ? 'border-red-500' : 'border-edge'
                  } bg-white`}
                  placeholder="10,000"
                />
              </div>

              {/* Amount Offset */}
              <div className="space-y-0">
                <Label htmlFor="element-offset" className="font-inter text-sm font-medium leading-6 text-ink-secondary">
                  Amount Offset
                </Label>
                <Input
                  id="element-offset"
                  type="text"
                  inputMode="numeric"
                  value={formatWithCommas(elementOffset)}
                  onChange={(e) => {
                    updateFormData('elementOffset', stripNonNumeric(e.target.value));
                  }}
                  onFocus={() => handleFieldFocus('configuration-elementOffset')}
                  className={`h-[28px] flex-shrink-0 self-stretch rounded-md border ${
                    isFieldInvalid('elementOffset') ? 'border-red-500' : 'border-edge'
                  } bg-white`}
                  placeholder="0"
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Connected Forms */}
      <div className="space-y-0">
        <Label className="font-inter text-base font-medium leading-6 text-ink-secondary">
          Connected Forms
        </Label>
        <div>
          <MultiSelectDropdownNoCreate
            height={28}
            options={(availableForms || [])
              .sort((a, b) => a.formName.localeCompare(b.formName))
              .map((form) => ({
                value: form.formName,
                label: form.formName,
              }))}
            setItems={setConnectedForms}
            items={connectedForms}
            error={Boolean(isFieldInvalid('connectedForms'))}
          />
        </div>
      </div>
    </>
  );
}
